<script lang="ts">
	import {
		Button,
		Checkbox,
		Container,
		Radio,
		Slider,
		SliderV2,
		Text,
		ToggleV2,
		TokenV2,
	} from '@Shelly/shelly-design-system';
	import { capFirstChar, mathNormalize } from 'src/helpers/utilities';
	import { bindStickyBottomAction } from 'src/helpers/sticky_bottom_action';
	import { callBluTRV, getTrvComponentByDevice } from 'src/helpers/virtual_helpers/blutrv_helpers';
	import type { Device } from 'src/types/Device';
	import { format } from 'svelte-i18n';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import SettingGuide from 'src/components/DevicesSettings/SettingGuide.svelte';

	export let device: Device;
	let waitForResponse = false;

	const savedUi = getTrvComponentByDevice(device)?.config?.sys?.ui || {
		lock: false,
		flip: false,
		t_units: 'C',
		brightness: 1,
	};

	let lock = savedUi.lock;
	let flip = savedUi.flip;
	let tUnits: string = savedUi.t_units || 'C';
	const brightnessMin = 1;
	const brightnessMax = 7;
	const brightnessMarkers = [...Array(brightnessMax - brightnessMin + 1)].map((_, index) => ({
		value: brightnessMin + index,
		showLabel: true,
	}));
	let brightness = mathNormalize(savedUi.brightness, brightnessMin, brightnessMax);

	let lastSavedLock = lock;
	let lastSavedFlip = flip;
	let lastSavedTUnits = tUnits;
	let lastSavedBrightness = brightness;

	$: isDirty =
		lock !== lastSavedLock ||
		flip !== lastSavedFlip ||
		tUnits !== lastSavedTUnits ||
		brightness !== lastSavedBrightness;
	$: saveDisabled = waitForResponse || ($showNewLayout && !isDirty);

	const { stickyBottomAction, setStickyBottomAction } = bindStickyBottomAction();

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			setStickyBottomAction({
				onClick: saveInterface,
				loading: waitForResponse,
				disabled: saveDisabled,
				icon: 'fas fa-save',
			});
		} else {
			setStickyBottomAction(null);
		}
	}

	const saveInterface = async (): Promise<void> => {
		if ($showNewLayout && saveDisabled) return;
		waitForResponse = true;
		const res = await callBluTRV(device, 'Sys.SetConfig', {
			id: 0,
			config: {
				ui: {
					lock,
					flip,
					t_units: tUnits,
					brightness,
				},
			},
		});
		if (res?.isok) {
			lastSavedLock = lock;
			lastSavedFlip = flip;
			lastSavedTUnits = tUnits;
			lastSavedBrightness = brightness;
		}
		waitForResponse = false;
	};

	$: a11yProps = {
		sliderValueText: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
		ariaLabel: $format('label_set_brightness', { default: 'Set Brightness' }),
		ariaLabelValue: $format('current_brightness_value', {
			values: { value: brightness },
			default: `Current brightness value is ${brightness}`,
		}),
	};
</script>

{#if $showNewLayout}
	<div class="blu-trv-interface">
		<SettingGuide
			text={$format('settings_guide_trv_interface', {
				default:
					'These options only change the valve itself: the ring lock, screen rotation, on-device temperature unit, and display brightness. They do not change units in the app.',
			})}
		/>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_trv_lock', { default: 'Device lock' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_desc_trv_lock', {
					default:
						'When on, turning the ring will not change the target temperature. Use this so the setting cannot be changed from the valve.',
				})}
			</Text>
			<ToggleV2 size="m" bind:checked={lock} id="toggle-blu-trv-lock">
				{capFirstChar($format('enable', { default: 'Enable' }))}
			</ToggleV2>
		</Container>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_trv_display_flip', { default: 'Display rotation' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_desc_trv_display_flip', {
					default: 'Flip the screen 180° so the numbers stay upright after you mount the TRV.',
				})}
			</Text>
			<ToggleV2 size="m" bind:checked={flip} id="toggle-blu-trv-flip">
				{capFirstChar($format('enable', { default: 'Enable' }))}
			</ToggleV2>
		</Container>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_trv_display_units', { default: 'Temperature units' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_desc_trv_display_units', {
					default:
						'Choose Celsius or Fahrenheit for the temperature shown on the TRV display. This does not change units in the app.',
				})}
			</Text>
			<div class="d-flex">
				<TokenV2
					id="token-blu-trv-display-unit-c"
					selected={tUnits === 'C'}
					size="m"
					title="°C"
					class="mr-s"
					on:click={() => {
						tUnits = 'C';
					}}
				/>
				<TokenV2
					id="token-blu-trv-display-unit-f"
					selected={tUnits === 'F'}
					size="m"
					title="°F"
					on:click={() => {
						tUnits = 'F';
					}}
				/>
			</div>
		</Container>

		<Container padding="s" type="borderSolid" backgroundColor="color-base">
			<Text styleType="h2" class="mb-s">
				{$format('settings_heading_trv_brightness', { default: 'Display brightness' })}
			</Text>
			<Text styleType="body-small" class="mb-s text-color-muted">
				{$format('settings_desc_trv_brightness', {
					default: 'Set how bright the TRV screen is, from 1 (dim) to 7 (bright).',
				})}
			</Text>
			<SliderV2
				id="slider-blu-trv-brightness"
				{a11yProps}
				min={brightnessMin}
				max={brightnessMax}
				step={1}
				type="incremented"
				trackColor="color-electric-vehicle"
				unit=""
				lineMarkers={brightnessMarkers}
				bind:value={brightness}
				on:value_change={(event) => {
					brightness = event.detail.value;
				}}
			/>
		</Container>
	</div>
{:else}
	<Checkbox
		size="m"
		bind:checked={lock}
		class="flex-shrink-0 mr-s"
		info={$format('checkbox_blu_trv_interface_lock', {
			default: 'Disables the control of the device using its rotary knob',
		})}
		id="checkbox-6whj7qn"
	>
		{$format('checkbox_blu_trv_lock', { default: 'TRV lock' })}
	</Checkbox>
	<Checkbox
		size="m"
		bind:checked={flip}
		class="flex-shrink-0 mr-s"
		info={$format('checkbox_blu_trv_interface_display', {
			default: "Rotates the TRV's display by 180°",
		})}
		id="checkbox-pjrpj6n"
	>
		{$format('checkbox_blu_trv_display', { default: 'TRV display' })}
	</Checkbox>
	<Radio value="C" size="m" bind:selected={tUnits} id="radio-4gagmeq"
		>{$format('checkbox_blu_trv_temp_celsius', {
			default: 'Temperature in °C',
		})}</Radio
	>
	<Radio value="F" size="m" class="mb-s" bind:selected={tUnits} id="radio-9zhag48"
		>{$format('checkbox_blu_trv_temp_fahrenheit', {
			default: 'Temperature in °F',
		})}</Radio
	>
	<Slider
		id="slider-jh4f00m"
		{a11yProps}
		title={`${capFirstChar($format('label_brightness', { default: 'brightness' }))}:`}
		min={1}
		max={7}
		type="incremented"
		class="mb-s"
		unit=""
		bind:value={brightness}
	/>

	<Button
		loading={waitForResponse}
		disabled={waitForResponse}
		class="mt-s"
		size="m"
		on:click={saveInterface}
		width="full"
		>{$format('button_save_settings', {
			default: 'Save settings',
		})}</Button
	>
{/if}

<style>
	.blu-trv-interface {
		display: flex;
		flex-direction: column;
		gap: 12px;
		width: 100%;
	}
</style>
